import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';

const Support = () => {
  const [activeTab, setActiveTab] = useState('tickets');
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [newTicket, setNewTicket] = useState({
    subject: '',
    category: 'general',
    priority: 'medium',
    description: '',
  });
  const [newReply, setNewReply] = useState('');

  // Mock tickets data - in production, fetch from API
  useEffect(() => {
    const timer = setTimeout(() => {
      setTickets([
        {
          id: 'TKT-001',
          subject: 'API rate limit exceeded',
          category: 'technical',
          priority: 'high',
          status: 'open',
          createdAt: '2026-01-22T14:30:00Z',
          updatedAt: '2026-01-23T09:15:00Z',
          messages: [
            { id: 1, from: 'user', message: 'I\'m getting rate limit errors when running scans. My plan should allow 5000 scans but I\'m blocked at 200.', timestamp: '2026-01-22T14:30:00Z' },
            { id: 2, from: 'support', message: 'Hi! I can see the issue. Your rate limits were incorrectly set. I\'ve corrected this and you should be able to resume scanning now.', timestamp: '2026-01-23T09:15:00Z' },
          ],
        },
        {
          id: 'TKT-002',
          subject: 'How to connect Amazon seller account?',
          category: 'billing',
          priority: 'medium',
          status: 'resolved',
          createdAt: '2026-01-20T10:00:00Z',
          updatedAt: '2026-01-20T12:30:00Z',
          messages: [
            { id: 1, from: 'user', message: 'I need help connecting my Amazon seller account to the platform.', timestamp: '2026-01-20T10:00:00Z' },
            { id: 2, from: 'support', message: 'Sure! Go to Settings > Integrations > Amazon. Click "Connect Account" and follow the OAuth flow. Let me know if you have any issues!', timestamp: '2026-01-20T12:30:00Z' },
          ],
        },
      ]);
    }, 0);
    return () => clearTimeout(timer);
  }, []);

  const categories = [
    { id: 'general', name: 'General Inquiry', icon: '❓' },
    { id: 'technical', name: 'Technical Issue', icon: '🔧' },
    { id: 'billing', name: 'Billing & Payments', icon: '💳' },
    { id: 'feature', name: 'Feature Request', icon: '💡' },
    { id: 'bug', name: 'Bug Report', icon: '🐛' },
  ];

  const faqs = [
    {
      question: 'How do I upgrade my plan?',
      answer: 'Go to Billing page and click "Change Plan". Select your new plan and complete the checkout process. Changes take effect immediately.',
    },
    {
      question: 'How many marketplaces can I scan?',
      answer: 'The number of marketplaces depends on your plan. Free: 3, Pro: 30, Business: All 83+, Enterprise: All + Beta access.',
    },
    {
      question: 'How do I cancel my subscription?',
      answer: 'Go to Billing > Cancel Subscription. You\'ll retain access until the end of your current billing period.',
    },
    {
      question: 'Can I export my data?',
      answer: 'Yes! Go to Settings > Data Export. You can export all your scan data, analytics, and reports in CSV or JSON format.',
    },
    {
      question: 'How do AI agents work?',
      answer: 'AI agents continuously monitor marketplaces for price changes, new listings, and arbitrage opportunities. They run 24/7 and alert you when they find matches.',
    },
  ];

  const handleCreateTicket = async () => {
    if (!newTicket.subject || !newTicket.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    const ticket = {
      id: `TKT-${String(tickets.length + 1).padStart(3, '0')}`,
      ...newTicket,
      status: 'open',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      messages: [
        { id: 1, from: 'user', message: newTicket.description, timestamp: new Date().toISOString() },
      ],
    };

    setTickets([ticket, ...tickets]);
    setShowNewTicket(false);
    setNewTicket({ subject: '', category: 'general', priority: 'medium', description: '' });
    toast.success('Ticket created successfully! Our team will respond within 24 hours.');
  };

  const handleReply = () => {
    if (!newReply.trim()) return;

    const updatedTickets = tickets.map((t) => {
      if (t.id === selectedTicket.id) {
        return {
          ...t,
          updatedAt: new Date().toISOString(),
          messages: [
            ...t.messages,
            { id: t.messages.length + 1, from: 'user', message: newReply, timestamp: new Date().toISOString() },
          ],
        };
      }
      return t;
    });

    setTickets(updatedTickets);
    setSelectedTicket(updatedTickets.find((t) => t.id === selectedTicket.id));
    setNewReply('');
    toast.success('Reply sent!');
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: { bg: 'rgba(59, 130, 246, 0.2)', color: '#3b82f6' },
      'in-progress': { bg: 'rgba(251, 191, 36, 0.2)', color: '#fbbf24' },
      resolved: { bg: 'rgba(34, 197, 94, 0.2)', color: '#22c55e' },
      closed: { bg: 'rgba(107, 114, 128, 0.2)', color: '#6b7280' },
    };
    const s = styles[status] || styles.open;
    return (
      <span style={{
        padding: '4px 10px',
        background: s.bg,
        color: s.color,
        borderRadius: '12px',
        fontSize: '0.75rem',
        fontWeight: '600',
        textTransform: 'capitalize',
      }}>
        {status.replace('-', ' ')}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      low: { bg: 'rgba(34, 197, 94, 0.2)', color: '#22c55e' },
      medium: { bg: 'rgba(251, 191, 36, 0.2)', color: '#fbbf24' },
      high: { bg: 'rgba(239, 68, 68, 0.2)', color: '#ef4444' },
      urgent: { bg: 'rgba(239, 68, 68, 0.3)', color: '#ef4444' },
    };
    const s = styles[priority] || styles.medium;
    return (
      <span style={{
        padding: '4px 10px',
        background: s.bg,
        color: s.color,
        borderRadius: '12px',
        fontSize: '0.75rem',
        fontWeight: '600',
        textTransform: 'capitalize',
      }}>
        {priority}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="support-page" style={{ padding: '30px' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '30px',
        flexWrap: 'wrap',
        gap: '16px',
      }}>
        <div>
          <h1 style={{
            fontSize: '2rem',
            fontWeight: 'bold',
            marginBottom: '8px',
            background: 'linear-gradient(135deg, #667eea, #00d4ff)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}>
            Support Center
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.6)' }}>
            Get help from our team or browse FAQs
          </p>
        </div>
        <button
          onClick={() => setShowNewTicket(true)}
          style={{
            padding: '12px 24px',
            background: 'linear-gradient(135deg, #667eea, #00d4ff)',
            border: 'none',
            borderRadius: '10px',
            color: 'white',
            cursor: 'pointer',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <span>+</span> New Ticket
        </button>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: '8px',
        marginBottom: '24px',
        borderBottom: '1px solid rgba(102, 126, 234, 0.2)',
        paddingBottom: '12px',
      }}>
        {[
          { id: 'tickets', label: 'My Tickets', icon: '📋' },
          { id: 'faq', label: 'FAQ', icon: '❓' },
          { id: 'contact', label: 'Contact', icon: '📧' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '10px 20px',
              background: activeTab === tab.id ? 'rgba(102, 126, 234, 0.2)' : 'transparent',
              border: activeTab === tab.id ? '1px solid rgba(102, 126, 234, 0.3)' : '1px solid transparent',
              borderRadius: '8px',
              color: activeTab === tab.id ? '#667eea' : 'rgba(255,255,255,0.6)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <span>{tab.icon}</span>
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tickets Tab */}
      {activeTab === 'tickets' && !selectedTicket && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          {tickets.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '60px 20px',
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(102, 126, 234, 0.2)',
              borderRadius: '16px',
            }}>
              <div style={{ fontSize: '3rem', marginBottom: '16px' }}>🎫</div>
              <h3 style={{ marginBottom: '8px' }}>No tickets yet</h3>
              <p style={{ color: 'rgba(255,255,255,0.5)', marginBottom: '20px' }}>
                Create a ticket if you need help with anything
              </p>
              <button
                onClick={() => setShowNewTicket(true)}
                style={{
                  padding: '12px 24px',
                  background: 'linear-gradient(135deg, #667eea, #00d4ff)',
                  border: 'none',
                  borderRadius: '10px',
                  color: 'white',
                  cursor: 'pointer',
                }}
              >
                Create Ticket
              </button>
            </div>
          ) : (
            <div style={{
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(102, 126, 234, 0.2)',
              borderRadius: '16px',
              overflow: 'hidden',
            }}>
              {tickets.map((ticket, i) => (
                <div
                  key={ticket.id}
                  onClick={() => setSelectedTicket(ticket)}
                  style={{
                    padding: '20px 24px',
                    borderBottom: i < tickets.length - 1 ? '1px solid rgba(102, 126, 234, 0.1)' : 'none',
                    cursor: 'pointer',
                    transition: 'background 0.2s',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(102, 126, 234, 0.1)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '8px',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem' }}>{ticket.id}</span>
                      <h4 style={{ fontWeight: '600' }}>{ticket.subject}</h4>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {getStatusBadge(ticket.status)}
                      {getPriorityBadge(ticket.priority)}
                    </div>
                  </div>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    color: 'rgba(255,255,255,0.5)',
                    fontSize: '0.85rem',
                  }}>
                    <span>{categories.find((c) => c.id === ticket.category)?.name}</span>
                    <span>Updated {formatDate(ticket.updatedAt)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      )}

      {/* Ticket Detail View */}
      {activeTab === 'tickets' && selectedTicket && (
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
        >
          <button
            onClick={() => setSelectedTicket(null)}
            style={{
              padding: '8px 16px',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(102, 126, 234, 0.3)',
              borderRadius: '8px',
              color: 'white',
              cursor: 'pointer',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            ← Back to Tickets
          </button>

          <div style={{
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(102, 126, 234, 0.2)',
            borderRadius: '16px',
            overflow: 'hidden',
          }}>
            {/* Ticket Header */}
            <div style={{
              padding: '24px',
              borderBottom: '1px solid rgba(102, 126, 234, 0.2)',
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '12px',
              }}>
                <div>
                  <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.9rem' }}>{selectedTicket.id}</span>
                  <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginTop: '4px' }}>
                    {selectedTicket.subject}
                  </h2>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {getStatusBadge(selectedTicket.status)}
                  {getPriorityBadge(selectedTicket.priority)}
                </div>
              </div>
              <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.9rem' }}>
                Created {formatDate(selectedTicket.createdAt)}
              </div>
            </div>

            {/* Messages */}
            <div style={{ padding: '24px', maxHeight: '400px', overflowY: 'auto' }}>
              {selectedTicket.messages.map((msg) => (
                <div
                  key={msg.id}
                  style={{
                    marginBottom: '20px',
                    display: 'flex',
                    flexDirection: msg.from === 'user' ? 'row-reverse' : 'row',
                    gap: '12px',
                  }}
                >
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    background: msg.from === 'user'
                      ? 'linear-gradient(135deg, #667eea, #00d4ff)'
                      : 'linear-gradient(135deg, #22c55e, #10b981)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    {msg.from === 'user' ? '👤' : '🎧'}
                  </div>
                  <div style={{
                    maxWidth: '70%',
                    padding: '16px',
                    background: msg.from === 'user'
                      ? 'rgba(102, 126, 234, 0.2)'
                      : 'rgba(34, 197, 94, 0.1)',
                    borderRadius: '12px',
                    borderTopRightRadius: msg.from === 'user' ? '0' : '12px',
                    borderTopLeftRadius: msg.from === 'support' ? '0' : '12px',
                  }}>
                    <div style={{
                      fontSize: '0.8rem',
                      color: 'rgba(255,255,255,0.5)',
                      marginBottom: '8px',
                    }}>
                      {msg.from === 'user' ? 'You' : 'Support Team'} • {formatDate(msg.timestamp)}
                    </div>
                    <div style={{ lineHeight: '1.5' }}>{msg.message}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Reply Box */}
            {selectedTicket.status !== 'closed' && (
              <div style={{
                padding: '24px',
                borderTop: '1px solid rgba(102, 126, 234, 0.2)',
              }}>
                <textarea
                  value={newReply}
                  onChange={(e) => setNewReply(e.target.value)}
                  placeholder="Type your reply..."
                  style={{
                    width: '100%',
                    padding: '16px',
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(102, 126, 234, 0.3)',
                    borderRadius: '10px',
                    color: 'white',
                    minHeight: '100px',
                    resize: 'vertical',
                    marginBottom: '16px',
                  }}
                />
                <button
                  onClick={handleReply}
                  disabled={!newReply.trim()}
                  style={{
                    padding: '12px 24px',
                    background: newReply.trim()
                      ? 'linear-gradient(135deg, #667eea, #00d4ff)'
                      : 'rgba(255,255,255,0.1)',
                    border: 'none',
                    borderRadius: '10px',
                    color: 'white',
                    cursor: newReply.trim() ? 'pointer' : 'not-allowed',
                    fontWeight: '600',
                  }}
                >
                  Send Reply
                </button>
              </div>
            )}
          </div>
        </motion.div>
      )}

      {/* FAQ Tab */}
      {activeTab === 'faq' && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
          }}
        >
          {faqs.map((faq, i) => (
            <details
              key={i}
              style={{
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(102, 126, 234, 0.2)',
                borderRadius: '12px',
                overflow: 'hidden',
              }}
            >
              <summary style={{
                padding: '20px 24px',
                cursor: 'pointer',
                fontWeight: '500',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}>
                {faq.question}
              </summary>
              <div style={{
                padding: '0 24px 20px',
                color: 'rgba(255,255,255,0.7)',
                lineHeight: '1.6',
              }}>
                {faq.answer}
              </div>
            </details>
          ))}
        </motion.div>
      )}

      {/* Contact Tab */}
      {activeTab === 'contact' && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '24px',
          }}
        >
          <div style={{
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(102, 126, 234, 0.2)',
            borderRadius: '16px',
            padding: '30px',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '16px' }}>📧</div>
            <h3 style={{ marginBottom: '8px' }}>Email Support</h3>
            <p style={{ color: 'rgba(255,255,255,0.6)', marginBottom: '16px' }}>
              Get a response within 24 hours
            </p>
            <a
              href="mailto:support@quantrolix.ai"
              style={{
                color: '#00d4ff',
                textDecoration: 'none',
                fontWeight: '500',
              }}
            >
              support@quantrolix.ai
            </a>
          </div>

          <div style={{
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(102, 126, 234, 0.2)',
            borderRadius: '16px',
            padding: '30px',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '16px' }}>💬</div>
            <h3 style={{ marginBottom: '8px' }}>Live Chat</h3>
            <p style={{ color: 'rgba(255,255,255,0.6)', marginBottom: '16px' }}>
              Available Mon-Fri, 9am-6pm EST
            </p>
            <button style={{
              padding: '10px 20px',
              background: 'linear-gradient(135deg, #667eea, #00d4ff)',
              border: 'none',
              borderRadius: '8px',
              color: 'white',
              cursor: 'pointer',
              fontWeight: '500',
            }}>
              Start Chat
            </button>
          </div>

          <div style={{
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(102, 126, 234, 0.2)',
            borderRadius: '16px',
            padding: '30px',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '16px' }}>📚</div>
            <h3 style={{ marginBottom: '8px' }}>Documentation</h3>
            <p style={{ color: 'rgba(255,255,255,0.6)', marginBottom: '16px' }}>
              Comprehensive guides and tutorials
            </p>
            <a
              href="https://docs.quantrolix.ai"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: '#00d4ff',
                textDecoration: 'none',
                fontWeight: '500',
              }}
            >
              docs.quantrolix.ai →
            </a>
          </div>
        </motion.div>
      )}

      {/* New Ticket Modal */}
      <AnimatePresence>
        {showNewTicket && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(10, 10, 15, 0.95)',
              backdropFilter: 'blur(10px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10000,
              padding: '20px',
            }}
            onClick={() => setShowNewTicket(false)}
          >
            <motion.div
              initial={{ scale: 0.95, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 20 }}
              style={{
                background: 'rgba(10, 10, 15, 0.95)',
                border: '1px solid rgba(102, 126, 234, 0.3)',
                borderRadius: '20px',
                padding: '40px',
                maxWidth: '600px',
                width: '100%',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '30px',
              }}>
                <h2 style={{
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  background: 'linear-gradient(135deg, #667eea, #00d4ff)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}>
                  Create New Ticket
                </h2>
                <button
                  onClick={() => setShowNewTicket(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'white',
                    fontSize: '1.5rem',
                    cursor: 'pointer',
                  }}
                >
                  ×
                </button>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', color: 'rgba(255,255,255,0.8)' }}>
                    Subject *
                  </label>
                  <input
                    type="text"
                    value={newTicket.subject}
                    onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                    placeholder="Brief description of your issue"
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(102, 126, 234, 0.3)',
                      borderRadius: '10px',
                      color: 'white',
                    }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', color: 'rgba(255,255,255,0.8)' }}>
                      Category
                    </label>
                    <select
                      value={newTicket.category}
                      onChange={(e) => setNewTicket({ ...newTicket, category: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        background: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(102, 126, 234, 0.3)',
                        borderRadius: '10px',
                        color: 'white',
                      }}
                    >
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id} style={{ background: '#1a1a2e' }}>
                          {cat.icon} {cat.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', color: 'rgba(255,255,255,0.8)' }}>
                      Priority
                    </label>
                    <select
                      value={newTicket.priority}
                      onChange={(e) => setNewTicket({ ...newTicket, priority: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        background: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(102, 126, 234, 0.3)',
                        borderRadius: '10px',
                        color: 'white',
                      }}
                    >
                      <option value="low" style={{ background: '#1a1a2e' }}>Low</option>
                      <option value="medium" style={{ background: '#1a1a2e' }}>Medium</option>
                      <option value="high" style={{ background: '#1a1a2e' }}>High</option>
                      <option value="urgent" style={{ background: '#1a1a2e' }}>Urgent</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '8px', color: 'rgba(255,255,255,0.8)' }}>
                    Description *
                  </label>
                  <textarea
                    value={newTicket.description}
                    onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
                    placeholder="Please describe your issue in detail..."
                    style={{
                      width: '100%',
                      padding: '16px',
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(102, 126, 234, 0.3)',
                      borderRadius: '10px',
                      color: 'white',
                      minHeight: '150px',
                      resize: 'vertical',
                    }}
                  />
                </div>

                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => setShowNewTicket(false)}
                    style={{
                      padding: '12px 24px',
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(102, 126, 234, 0.3)',
                      borderRadius: '10px',
                      color: 'white',
                      cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateTicket}
                    style={{
                      padding: '12px 24px',
                      background: 'linear-gradient(135deg, #667eea, #00d4ff)',
                      border: 'none',
                      borderRadius: '10px',
                      color: 'white',
                      cursor: 'pointer',
                      fontWeight: '600',
                    }}
                  >
                    Create Ticket
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Support;
